import React, { useState, useMemo, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  X, Loader2, ImagePlus, Film, Sparkles, Download, ChevronDown, ChevronUp,
  Check, Cpu, Search, Volume2, AudioLines, Play, Layers, Plus,
  Image as ImageIcon, Video as VideoIcon, Mic, User as UserIcon,
  Wand2, Edit3, Trash2, Clock, MonitorPlay,
} from "lucide-react";
import { AppShell } from "../components/AppShell";
import { BuyCreditsDialog } from "../components/BuyCreditsDialog";
import { fadeUp, stagger, scaleIn, chipPop } from "../lib/motions";

// ────────────────────────────────────────────────────────────
// Magnific-style Model picker modal
// ────────────────────────────────────────────────────────────
function ModelPickerModal({ open, onOpenChange, models, value, onChange }) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});
  const [showAllFeatured, setShowAllFeatured] = useState(false);

  // Group by vendor
  const grouped = useMemo(() => {
    const map = {};
    models.forEach((m) => {
      if (!map[m.vendor]) map[m.vendor] = [];
      map[m.vendor].push(m);
    });
    return map;
  }, [models]);

  const featured = useMemo(() => {
    const list = [{ id: "auto", name: "Auto", vendor: "AI Bazaar", available: true, tags: ["Smart routing"], _auto: true }];
    return list.concat(models.filter((m) => m.featured));
  }, [models]);

  const filteredFeatured = featured.filter((m) => m.name.toLowerCase().includes(query.toLowerCase()));

  const filteredGrouped = useMemo(() => {
    // Hide 'featured' models from the vendor groups — they already show at the top
    const featuredIds = new Set(models.filter((m) => m.featured).map((m) => m.id));
    const withoutFeatured = {};
    Object.entries(grouped).forEach(([v, list]) => {
      const rest = list.filter((m) => !featuredIds.has(m.id));
      if (rest.length) withoutFeatured[v] = rest;
    });
    if (!query) return withoutFeatured;
    const out = {};
    Object.entries(withoutFeatured).forEach(([v, list]) => {
      const filtered = list.filter((m) => m.name.toLowerCase().includes(query.toLowerCase()));
      if (filtered.length) out[v] = filtered;
    });
    return out;
  }, [grouped, models, query]);

  const select = (m) => {
    if (!m.available) return;
    onChange(m.id);
    onOpenChange(false);
    setQuery("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-lg w-[calc(100vw-2rem)] max-h-[85vh] flex flex-col overflow-hidden bg-zinc-950/98 backdrop-blur-2xl border border-white/10 rounded-3xl"
        data-testid="model-picker-modal"
      >
        <DialogHeader className="p-5 pb-3 border-b border-white/5 shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-display font-extrabold tracking-tight text-white">Model</DialogTitle>
          </div>
          <DialogDescription className="sr-only">Choose a video generation model from the list below.</DialogDescription>
        </DialogHeader>

        {/* Search */}
        <div className="p-4 pb-2 shrink-0">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              data-testid="model-search"
              autoFocus
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-11 pl-10 bg-white/[0.04] border-white/10 rounded-2xl focus-visible:border-cyan-500/50 focus-visible:ring-cyan-500/20 text-sm"
            />
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4" data-testid="model-picker-scroll">
          {/* Featured */}
          {filteredFeatured.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 px-2 py-3">Featured</div>
              <div className="space-y-1">
                {filteredFeatured.map((m) => (
                  <ModelRow key={m.id} m={m} active={m.id === value} onSelect={() => select(m)} testId={`model-option-${m.id}`} />
                ))}
              </div>
            </>
          )}

          {/* All models grouped by vendor */}
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 px-2 py-3 mt-2">All Models</div>
          <div className="space-y-1">
            {Object.entries(filteredGrouped).map(([vendor, list]) => (
              <div key={vendor}>
                <button
                  data-testid={`vendor-group-${vendor}`}
                  onClick={() => setExpanded((p) => ({ ...p, [vendor]: p[vendor] === false }))}
                  className="w-full flex items-center justify-between px-3 py-3.5 rounded-xl hover:bg-white/[0.04] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <VendorIcon vendor={vendor} />
                    <span className="text-base font-medium text-white">{vendor}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">{list.length} model{list.length > 1 ? "s" : ""}</span>
                    <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform duration-200 ${expanded[vendor] !== false || query ? "rotate-180" : ""}`} />
                  </div>
                </button>
                <AnimatePresence>
                  {(expanded[vendor] !== false || query) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden pl-6"
                    >
                      {list.map((m) => (
                        <ModelRow key={m.id} m={m} active={m.id === value} onSelect={() => select(m)} testId={`model-option-${m.id}`} />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function VendorIcon({ vendor }) {
  const map = {
    ByteDance: "bg-orange-500/20 text-orange-400",
    Google: "bg-blue-500/20 text-blue-400",
    "Wan-AI": "bg-emerald-500/20 text-emerald-400",
    Pixverse: "bg-fuchsia-500/20 text-fuchsia-400",
    PrunaAI: "bg-teal-500/20 text-teal-400",
    Lightricks: "bg-amber-500/20 text-amber-400",
    FastVideo: "bg-cyan-500/20 text-cyan-400",
    Kuaishou: "bg-rose-500/20 text-rose-400",
    OpenAI: "bg-lime-500/20 text-lime-400",
  };
  const cls = map[vendor] || "bg-white/10 text-zinc-400";
  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cls}`}>
      <span className="text-xs font-bold">{vendor[0]}</span>
    </div>
  );
}

function ModelRow({ m, active, onSelect, testId }) {
  const disabled = !m.available;
  const Icon = m._auto ? Cpu : MonitorPlay;
  return (
    <button
      data-testid={testId}
      disabled={disabled}
      onClick={onSelect}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors duration-150 ${
        active ? "bg-white/[0.08] border border-white/15" : "border border-transparent hover:bg-white/[0.04]"
      } ${disabled ? "opacity-45 cursor-not-allowed" : ""}`}
    >
      <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-zinc-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-base font-medium text-white truncate">{m.name}</span>
          {disabled && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 uppercase tracking-wider">Soon</span>
          )}
        </div>
      </div>
      {active && <Check className="w-4 h-4 text-fuchsia-400 shrink-0" />}
    </button>
  );
}

// ────────────────────────────────────────────────────────────
// Image upload card (Start/End)
// ────────────────────────────────────────────────────────────
function ImageUploadCard({ label, icon: Icon, image, uploading = false, onUpload, onClear, testId }) {
  const inputRef = useRef(null);
  return (
    <div
      onClick={() => !image && !uploading && inputRef.current?.click()}
      className={`relative aspect-[4/3] rounded-2xl border-2 border-dashed border-white/15 hover:border-white/30 transition-colors overflow-hidden group ${
        image || uploading ? "border-solid border-white/20 cursor-default" : "cursor-pointer"
      }`}
      data-testid={testId}
    >
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])} data-testid={`${testId}-input`} />
      {uploading ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/[0.04]" data-testid={`${testId}-uploading`}>
          {/* Animated shimmer bar */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-fuchsia-500/20 to-transparent animate-[shimmer_1.6s_linear_infinite]" />
          </div>
          <div className="relative w-10 h-10 rounded-full bg-fuchsia-500/15 border border-fuchsia-500/40 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-fuchsia-300 animate-spin" strokeWidth={2.4} />
          </div>
          <div className="relative text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Uploading…</div>
        </div>
      ) : image ? (
        <>
          <img src={image.url} alt="" className="w-full h-full object-cover" />
          <button
            data-testid={`${testId}-clear`}
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 backdrop-blur flex items-center justify-center hover:bg-red-500/80"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-zinc-500 group-hover:text-zinc-300 transition-colors">
          <Icon className="w-6 h-6" strokeWidth={1.5} />
          <span className="text-sm font-medium">{label}</span>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Add-media round icon button
// ────────────────────────────────────────────────────────────
function MediaIconButton({ icon: Icon, onClick, title, testId, active }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      title={title}
      className={`w-10 h-10 rounded-full flex items-center justify-center border transition-colors duration-200 ${
        active
          ? "bg-fuchsia-500/15 border-fuchsia-500/40 text-fuchsia-300"
          : "bg-white/[0.04] border-white/10 hover:border-white/25 hover:bg-white/[0.07] text-zinc-300"
      }`}
    >
      <Icon className="w-4 h-4" strokeWidth={2} />
    </button>
  );
}

// ────────────────────────────────────────────────────────────
// Main Studio component
// ────────────────────────────────────────────────────────────
export default function Studio() {
  const { user, setUser } = useAuth();
  const qc = useQueryClient();
  const { data: modelsData } = useQuery({
    queryKey: ["models"],
    queryFn: async () => (await api.get("/models")).data,
  });
  const models = useMemo(() => modelsData?.models || [], [modelsData]);

  const [modelId, setModelId] = useState("auto");
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [resolution, setResolution] = useState("720p");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [duration, setDuration] = useState(5);
  const [generateAudio, setGenerateAudio] = useState(true);
  const [startImage, setStartImage] = useState(null);
  const [endImage, setEndImage] = useState(null);
  const [extraRefs, setExtraRefs] = useState([]); // images
  const [refVideos, setRefVideos] = useState([]);
  // Per-slot upload progress
  const [uploading, setUploading] = useState({ start: false, end: false, extras: 0, videos: 0 });
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const extraImgInputRef = useRef(null);
  const vidInputRef = useRef(null);

  // Determine the effective model (respecting Auto)
  const currentModel = useMemo(() => {
    if (modelId === "auto") {
      return { id: "auto", name: "Auto", vendor: "AI Bazaar", resolutions: ["480p","720p","1080p"], aspect_ratios: ["16:9","9:16","1:1"], duration_range: [4, 15], supports_reference_images: true, supports_reference_videos: true, supports_audio: true };
    }
    return models.find((m) => m.id === modelId);
  }, [modelId, models]);

  useEffect(() => {
    if (!currentModel) return;
    if (currentModel.resolutions && !currentModel.resolutions.includes(resolution)) setResolution(currentModel.resolutions[0]);
    if (currentModel.aspect_ratios && !currentModel.aspect_ratios.includes(aspectRatio)) setAspectRatio(currentModel.aspect_ratios[0]);
    const [minD, maxD] = currentModel.duration_range || [4, 15];
    if (duration < minD) setDuration(minD);
    if (duration > maxD) setDuration(maxD);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelId]);

  const modelLabel = currentModel?.name || "Auto";

  const uploadMutation = useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      return data;
    },
    onError: (err) => toast.error(err.response?.data?.detail || "Upload failed"),
  });

  const uploadForSlot = async (file, slot) => {
    // Mark slot as uploading
    if (slot === "start" || slot === "end") {
      setUploading((p) => ({ ...p, [slot]: true }));
    } else if (slot === "extra") {
      setUploading((p) => ({ ...p, extras: p.extras + 1 }));
    } else if (slot === "video") {
      setUploading((p) => ({ ...p, videos: p.videos + 1 }));
    }
    try {
      const data = await uploadMutation.mutateAsync(file);
      if (slot === "start") setStartImage(data);
      else if (slot === "end") setEndImage(data);
      else if (slot === "extra") setExtraRefs((p) => [...p, data]);
      else if (slot === "video") setRefVideos((p) => [...p, data]);
      toast.success("Reference attached");
    } catch (e) { /* handled by uploadMutation onError */ }
    finally {
      if (slot === "start" || slot === "end") {
        setUploading((p) => ({ ...p, [slot]: false }));
      } else if (slot === "extra") {
        setUploading((p) => ({ ...p, extras: Math.max(0, p.extras - 1) }));
      } else if (slot === "video") {
        setUploading((p) => ({ ...p, videos: Math.max(0, p.videos - 1) }));
      }
    }
  };

  const [pendingJobId, setPendingJobId] = useState(null);
  const [buyCreditsOpen, setBuyCreditsOpen] = useState(false);
  const [creditsInfo, setCreditsInfo] = useState({ needed: null, available: null, reason: null });

  // Poll job while pending
  const { data: pollJobData } = useQuery({
    queryKey: ["job", pendingJobId],
    queryFn: async () => (await api.get(`/jobs/${pendingJobId}`)).data,
    enabled: !!pendingJobId,
    refetchInterval: (query) => {
      const status = query?.state?.data?.job?.status;
      return status === "processing" ? 4000 : false;
    },
  });
  const activeJob = pollJobData?.job;

  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status === "succeeded") {
      toast.success("Video ready");
      qc.invalidateQueries({ queryKey: ["jobs"] });
      api.get("/auth/me").then(({ data }) => setUser(data.user));
    } else if (activeJob.status === "failed") {
      const msg = activeJob.error || "Generation failed";
      toast.error(msg, { duration: 12000, style: { maxWidth: 480 } });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      api.get("/auth/me").then(({ data }) => setUser(data.user));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob?.status]);

  const generateMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post("/generate", {
        model_id: modelId,
        prompt,
        resolution,
        aspect_ratio: aspectRatio,
        duration,
        generate_audio: generateAudio,
        start_image: startImage?.path || null,
        end_image: endImage?.path || null,
        reference_images: extraRefs.map((r) => r.path),
        reference_videos: refVideos.map((r) => r.path),
      });
      return data.job;
    },
    onSuccess: (job) => {
      // Start polling this job
      setPendingJobId(job.id);
      toast("Queued — video will appear here when ready", { duration: 4000 });
    },
    onError: (err) => {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Generation failed";
      if (status === 402) {
        // Parse "Not enough credits. Need X, have Y."
        const match = /Need (\d+),\s*have (\d+)/i.exec(detail);
        setCreditsInfo({
          needed: match ? parseInt(match[1], 10) : null,
          available: match ? parseInt(match[2], 10) : (user?.credits ?? 0),
          reason: detail,
        });
        setBuyCreditsOpen(true);
      } else {
        toast.error(detail);
      }
    },
  });

  const { data: jobsData } = useQuery({
    queryKey: ["jobs"],
    queryFn: async () => (await api.get("/jobs?limit=8")).data,
    refetchInterval: 20000,
  });
  const recentJobs = jobsData?.jobs || [];
  const lastJob = activeJob || generateMutation.data;
  const isProcessing = generateMutation.isPending || (activeJob?.status === "processing");

  const canGenerate = !!prompt.trim() && !isProcessing;

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Top banner: Video Generator */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="rounded-2xl bg-gradient-to-br from-fuchsia-500/10 via-white/[0.03] to-cyan-500/10 border border-white/10 p-4 md:p-5 mb-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1">
              <span>Tools /</span>
            </div>
            <h1 data-testid="studio-heading" className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-white">
              Video Generator
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10">
              <Sparkles className="w-3 h-3 text-cyan-400" />
              <span data-testid="studio-credits" className="font-mono text-white text-xs">{user?.credits ?? 0}</span>
            </div>
          </div>
        </motion.div>

        {/* ── MODEL ── */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.05 }} className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-semibold mb-2.5">Model</div>
          <button
            data-testid="model-picker-trigger"
            onClick={() => setModelPickerOpen(true)}
            className="w-full flex items-center justify-between px-4 py-4 rounded-2xl bg-white/[0.04] border border-white/10 hover:border-white/25 hover:bg-white/[0.06] transition-colors group"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-cyan-500/20 border border-white/10 flex items-center justify-center">
                <Cpu className="w-4 h-4 text-white" />
              </div>
              <span className="text-base font-semibold text-white">{modelLabel}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          </button>
        </motion.section>

        {/* ── REFERENCES ── */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }} className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-semibold mb-2.5">References</div>

          {/* Start / End image cards */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            <ImageUploadCard
              label="Start image"
              icon={ImageIcon}
              image={startImage}
              uploading={uploading.start}
              onUpload={(f) => uploadForSlot(f, "start")}
              onClear={() => setStartImage(null)}
              testId="start-image"
            />
            <ImageUploadCard
              label="End image"
              icon={ImageIcon}
              image={endImage}
              uploading={uploading.end}
              onUpload={(f) => uploadForSlot(f, "end")}
              onClear={() => setEndImage(null)}
              testId="end-image"
            />
          </div>

          {/* Add media pills */}
          <div className="rounded-2xl border-2 border-dashed border-white/15 p-3 hover:border-white/25 transition-colors">
            <input ref={extraImgInputRef} type="file" accept="image/*" multiple hidden data-testid="extra-image-input"
              onChange={(e) => { Array.from(e.target.files || []).forEach((f) => uploadForSlot(f, "extra")); e.target.value = ""; }} />
            <input ref={vidInputRef} type="file" accept="video/*" multiple hidden data-testid="extra-video-input"
              onChange={(e) => { Array.from(e.target.files || []).forEach((f) => uploadForSlot(f, "video")); e.target.value = ""; }} />

            <div className="flex flex-wrap items-center gap-2">
              <button
                data-testid="add-image-btn"
                onClick={() => extraImgInputRef.current?.click()}
                disabled={uploading.extras > 0}
                className="flex items-center gap-2 h-11 px-4 rounded-full bg-white/[0.05] border border-white/10 hover:border-fuchsia-500/50 hover:bg-fuchsia-500/10 text-white text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-wait"
              >
                {uploading.extras > 0 ? (
                  <Loader2 className="w-4 h-4 text-fuchsia-400 animate-spin" />
                ) : (
                  <ImageIcon className="w-4 h-4 text-fuchsia-400" />
                )}
                <span>{uploading.extras > 0 ? "Uploading…" : "Reference image"}</span>
              </button>
              <button
                data-testid="add-video-btn"
                onClick={() => vidInputRef.current?.click()}
                disabled={uploading.videos > 0}
                className="flex items-center gap-2 h-11 px-4 rounded-full bg-white/[0.05] border border-white/10 hover:border-cyan-500/50 hover:bg-cyan-500/10 text-white text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-wait"
              >
                {uploading.videos > 0 ? (
                  <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                ) : (
                  <VideoIcon className="w-4 h-4 text-cyan-400" />
                )}
                <span>{uploading.videos > 0 ? "Uploading…" : "Reference video"}</span>
              </button>
              <span className="text-xs text-zinc-500 ml-auto pr-1">
                {extraRefs.length + refVideos.length} attached
                {(uploading.extras + uploading.videos) > 0 && (
                  <span className="ml-1 text-fuchsia-400">
                    (+{uploading.extras + uploading.videos} uploading)
                  </span>
                )}
              </span>
            </div>

            {(extraRefs.length > 0 || refVideos.length > 0 || uploading.extras > 0 || uploading.videos > 0) && (
              <div className="flex items-center gap-1.5 mt-3 overflow-x-auto pb-1">
                <AnimatePresence>
                  {extraRefs.map((r, i) => (
                    <motion.div key={r.id} variants={chipPop} initial="hidden" animate="show" exit="exit" layout
                      data-testid={`extra-ref-chip-${i}`}
                      className="shrink-0 relative w-14 h-14 rounded-lg overflow-hidden group border border-white/15">
                      <img src={r.url} className="w-full h-full object-cover" alt="" />
                      <button onClick={() => setExtraRefs((p) => p.filter((_, idx) => idx !== i))}
                        className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </motion.div>
                  ))}
                  {refVideos.map((r, i) => (
                    <motion.div key={r.id} variants={chipPop} initial="hidden" animate="show" exit="exit" layout
                      data-testid={`extra-video-chip-${i}`}
                      className="shrink-0 relative w-14 h-14 rounded-lg bg-white/[0.06] border border-white/15 flex items-center justify-center group">
                      <Film className="w-5 h-5 text-cyan-400" />
                      <button onClick={() => setRefVideos((p) => p.filter((_, idx) => idx !== i))}
                        className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-lg transition-opacity">
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </motion.div>
                  ))}
                  {/* Uploading placeholder chips */}
                  {Array.from({ length: uploading.extras }).map((_, i) => (
                    <motion.div
                      key={`up-img-${i}`}
                      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                      data-testid={`uploading-ref-chip-${i}`}
                      className="shrink-0 relative w-14 h-14 rounded-lg border border-fuchsia-500/30 bg-fuchsia-500/10 flex items-center justify-center overflow-hidden">
                      <div className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-white/15 to-transparent animate-[shimmer_1.4s_linear_infinite]" />
                      <Loader2 className="w-4 h-4 text-fuchsia-300 animate-spin relative" strokeWidth={2.4} />
                    </motion.div>
                  ))}
                  {Array.from({ length: uploading.videos }).map((_, i) => (
                    <motion.div
                      key={`up-vid-${i}`}
                      initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                      data-testid={`uploading-video-chip-${i}`}
                      className="shrink-0 relative w-14 h-14 rounded-lg border border-cyan-500/30 bg-cyan-500/10 flex items-center justify-center overflow-hidden">
                      <div className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-white/15 to-transparent animate-[shimmer_1.4s_linear_infinite]" />
                      <Loader2 className="w-4 h-4 text-cyan-300 animate-spin relative" strokeWidth={2.4} />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        </motion.section>

        {/* ── SHOT / PROMPT ── */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.15 }} className="mb-6">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Shot</span>
            <button data-testid="add-shot-btn" title="Add another shot (coming soon)"
              onClick={() => toast("Multi-shot coming soon")}
              className="w-6 h-6 rounded-full border border-white/15 flex items-center justify-center hover:bg-white/10 transition-colors">
              <Plus className="w-3 h-3 text-zinc-400" />
            </button>
          </div>
          <div className="rounded-2xl bg-white/[0.03] border border-white/10 overflow-hidden focus-within:border-cyan-500/40 transition-colors">
            <Textarea
              data-testid="prompt-input"
              value={prompt}
              onChange={(e) => e.target.value.length <= 1999 && setPrompt(e.target.value)}
              placeholder="Describe your scene. Reference your video or images using @img1, @vid1…"
              rows={5}
              className="bg-transparent border-0 focus-visible:ring-0 text-base resize-none placeholder:text-zinc-600 min-h-[140px] px-4 py-3.5"
            />
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/5">
              <div className="text-xs text-zinc-500 font-mono" data-testid="prompt-counter">{prompt.length}/1999</div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="enhance-prompt-btn"
                  onClick={() => toast("Prompt enhancer coming soon")}
                  className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                  title="Enhance prompt"
                >
                  <Wand2 className="w-4 h-4" />
                </button>
                <button
                  data-testid="clear-prompt-btn"
                  onClick={() => setPrompt("")}
                  className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                  title="Clear"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.section>

        {/* ── Advanced settings (collapsible) ── */}
        <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.2 }} className="mb-6">
          <button
            data-testid="advanced-toggle"
            onClick={() => setAdvancedOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/25 transition-colors"
          >
            <div className="flex items-center gap-2 text-sm text-white font-medium">
              <Layers className="w-4 h-4 text-zinc-400" />
              Advanced
            </div>
            <div className="flex items-center gap-3 text-xs text-zinc-400 font-mono">
              <span>{resolution}</span>
              <span>·</span>
              <span>{aspectRatio}</span>
              <span>·</span>
              <span>{duration}s</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${advancedOpen ? "rotate-180" : ""}`} />
            </div>
          </button>
          <AnimatePresence>
            {advancedOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.25 }}
                className="overflow-hidden"
              >
                <div className="mt-2 p-4 rounded-2xl bg-white/[0.02] border border-white/10 space-y-5">
                  {currentModel?.resolutions?.length > 1 && (
                    <SegmentedControl label="Resolution" options={currentModel.resolutions} value={resolution} onChange={setResolution} testId="resolution" />
                  )}
                  {currentModel?.aspect_ratios?.length > 1 && (
                    <AspectRatioGrid options={currentModel.aspect_ratios} value={aspectRatio} onChange={setAspectRatio} testId="aspect" />
                  )}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Duration</span>
                      <span className="text-sm text-white font-mono">{duration}s</span>
                    </div>
                    <Slider data-testid="duration-slider" value={[duration]}
                      min={currentModel?.duration_range?.[0] || 4}
                      max={currentModel?.duration_range?.[1] || 15}
                      step={1} onValueChange={(v) => setDuration(v[0])} />
                  </div>
                  {currentModel?.supports_audio && (
                    <div className="flex items-center justify-between pt-2 border-t border-white/5">
                      <div>
                        <div className="text-sm text-white font-medium">Generate audio</div>
                        <div className="text-xs text-zinc-500 mt-0.5">Native audio synced with video</div>
                      </div>
                      <Switch data-testid="audio-toggle" checked={generateAudio} onCheckedChange={setGenerateAudio} />
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>

        {/* ── Generate ── */}
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.25 }}>
          <Button
            data-testid="generate-btn"
            onClick={() => generateMutation.mutate()}
            disabled={!canGenerate}
            className="btn-primary w-full h-14 rounded-2xl font-semibold text-base"
          >
            {isProcessing ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Generating…</>
            ) : (
              <><Sparkles className="w-5 h-5 mr-2" /> Generate</>
            )}
          </Button>
        </motion.div>

        {/* Latest preview */}
        <AnimatePresence>
          {(lastJob?.video_url || isProcessing) && (
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.4 }}
              className="mt-8 rounded-3xl bg-white/[0.02] border border-white/10 overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b border-white/5">
                <div className="text-[10px] uppercase tracking-[0.24em] text-cyan-400">
                  {isProcessing ? "Generating…" : "Latest generation"}
                </div>
                {lastJob?.video_url && (
                  <a href={lastJob.video_url} download data-testid="preview-download"
                    className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300">
                    <Download className="w-3.5 h-3.5" /> Download
                  </a>
                )}
              </div>
              {isProcessing ? (
                <div className="aspect-video bg-black flex flex-col items-center justify-center gap-3 text-zinc-500">
                  <Loader2 className="w-8 h-8 animate-spin text-fuchsia-400" />
                  <div className="text-sm">Rendering your scene — this can take 1–5 minutes</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-600">You can safely leave this page</div>
                </div>
              ) : lastJob?.video_url ? (
                <video data-testid="preview-video" src={lastJob.video_url} controls playsInline autoPlay muted loop
                  className="w-full max-h-[540px] bg-black object-contain" />
              ) : null}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Recent strip */}
        {recentJobs.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }} className="mt-10">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Recent</div>
              <a href="/gallery" className="text-xs text-cyan-400 hover:text-cyan-300 uppercase tracking-widest">All →</a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {recentJobs.slice(0, 4).map((j) => (
                <a key={j.id} href="/gallery" data-testid={`recent-${j.id}`}
                  className="group relative aspect-video rounded-xl overflow-hidden bg-black border border-white/10 hover:border-white/25 transition-colors">
                  {j.status === "succeeded" && j.video_url ? (
                    <video src={j.video_url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs">
                      {j.status === "failed" ? "Failed" : <Loader2 className="w-4 h-4 animate-spin" />}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Model picker modal */}
      <ModelPickerModal
        open={modelPickerOpen}
        onOpenChange={setModelPickerOpen}
        models={models}
        value={modelId}
        onChange={setModelId}
      />

      {/* Buy credits dialog */}
      <BuyCreditsDialog
        open={buyCreditsOpen}
        onOpenChange={setBuyCreditsOpen}
        needed={creditsInfo.needed}
        available={creditsInfo.available}
        reason={creditsInfo.reason}
      />
    </AppShell>
  );
}

// Aspect ratio button with visual proportional shape
function AspectShape({ ratio, active }) {
  const [w, h] = ratio.split(":").map(Number);
  const maxSide = 22;
  const scale = maxSide / Math.max(w, h);
  return (
    <div className="flex items-center justify-center" style={{ width: 30, height: 24 }}>
      <div
        className={`rounded-[3px] transition-colors ${active ? "bg-white" : "bg-zinc-400"}`}
        style={{ width: `${w * scale}px`, height: `${h * scale}px` }}
      />
    </div>
  );
}

// Segmented control
function SegmentedControl({ label, options, value, onChange, testId }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">{label}</div>
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(options.length, 4)}, minmax(0, 1fr))` }}>
        {options.map((o) => (
          <button
            key={o}
            data-testid={`${testId}-${o}`}
            onClick={() => onChange(o)}
            className={`h-10 rounded-xl text-sm font-medium transition-colors duration-150 border ${
              o === value
                ? "bg-white/10 border-white/25 text-white"
                : "bg-transparent border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

// Aspect ratio grid with visual shape previews
function AspectRatioGrid({ options, value, onChange, testId }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-2">Aspect ratio</div>
      <div className="grid grid-cols-4 gap-1.5">
        {options.map((o) => {
          const active = o === value;
          return (
            <button
              key={o}
              data-testid={`${testId}-${o}`}
              onClick={() => onChange(o)}
              className={`h-16 rounded-xl border flex flex-col items-center justify-center gap-1 transition-colors duration-150 ${
                active
                  ? "bg-white/10 border-white/25 text-white"
                  : "bg-transparent border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
              }`}
            >
              <AspectShape ratio={o} active={active} />
              <span className="text-[11px] font-mono">{o}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

